import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, FileText, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import OfficerShell, { STATUS } from "./OfficerShell";

const STATUS_OPTIONS = [
  ["", "सर्व स्थिती"],
  ["DRAFT", "प्रारूप"],
  ["SUBMITTED", "प्राप्त"],
  ["UNDER_VERIFICATION", "पडताळणीत"],
  ["FORWARDED", "पुढे पाठवले"],
  ["APPROVED", "मंजूर"],
  ["REJECTED", "नामंजूर"],
  ["ISSUED", "निर्गमित"],
];

const CARDS = [
  ["total", "एकूण अर्ज", "text-[#1b5e3f]"],
  ["DRAFT", "प्रारूप", "text-slate-500"],
  ["SUBMITTED", "प्राप्त", "text-blue-600"],
  ["APPROVED", "मंजूर", "text-emerald-600"],
  ["REJECTED", "नामंजूर", "text-red-600"],
];

export default function OfficerApplications() {
  const navigate = useNavigate();
  const { user } = useApp();
  const isViewer = user?.role === "viewer";
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const data = await api.officerList(search, status, "", true);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const onSearch = (e) => { e.preventDefault(); load(); };

  const counts = rows.reduce((a, r) => { a.total++; a[r.status] = (a[r.status] || 0) + 1; return a; }, { total: 0 });

  return (
    <OfficerShell>
      <div className="mb-5">
        <h2 className="text-[20px] font-bold text-[#1b3d2a]">अर्ज</h2>
        <p className="text-[13px] text-slate-500">जिल्ह्यातील सर्व अर्ज पाहा व शोधा. तपशीलासाठी अर्जावर क्लिक करा.</p>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5" data-testid="officer-apps-stats">
        {CARDS.map(([k, label, cls]) => (
          <div key={k} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-[12px] font-semibold text-slate-500">{label}</p>
            <p className={`mt-1 text-[24px] font-bold ${cls}`}>{k === "total" ? counts.total : (counts[k] || 0)}</p>
          </div>
        ))}
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <form onSubmit={onSearch} className="flex flex-1 items-center gap-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="apps-search"
              placeholder="नाव / अर्ज क्रमांक / ई-मेल शोधा..."
              className="w-full rounded-full border border-slate-300 py-2.5 pl-9 pr-4 text-[14px] outline-none focus:border-[#1b5e3f]" />
          </div>
          <button type="submit" data-testid="apps-search-btn" className="rounded-full bg-[#1b5e3f] px-5 py-2.5 text-[14px] font-semibold text-white hover:bg-[#144d33]">शोधा</button>
        </form>
        <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="apps-status-filter"
          className="rounded-full border border-slate-300 px-4 py-2.5 text-[14px] outline-none focus:border-[#1b5e3f]">
          {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table className="w-full border-collapse text-[13.5px]">
          <thead>
            <tr className="bg-[#1b5e3f] text-left text-white">
              <th className="p-3 font-semibold">अर्ज क्रमांक</th>
              <th className="p-3 font-semibold">अर्जदाराचे नाव</th>
              <th className="p-3 font-semibold">तालुका</th>
              <th className="p-3 font-semibold">स्थानिक स्वराज्य संस्था</th>
              <th className="p-3 font-semibold">स्थिती</th>
              <th className="p-3 text-center font-semibold">तपशील</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">लोड होत आहे...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">कोणतेही अर्ज आढळले नाहीत.</td></tr>
            ) : rows.map((r) => {
              const st = STATUS[r.status] || { label: r.status, cls: "bg-slate-100 text-slate-600" };
              return (
                <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`app-row-${r.applicationNumber}`}>
                  <td className="p-3 font-latin font-semibold text-[#1b3d2a]">{r.applicationNumber}</td>
                  <td className="p-3">{r.data?.nameMr || r.applicantName || "—"}</td>
                  <td className="p-3">{r.tehsil || r.data?.taluka || "—"}</td>
                  <td className="p-3">{r.data?.village || r.data?.city || "—"}</td>
                  <td className="p-3"><span className={`rounded-full px-2.5 py-1 text-[12px] font-semibold ${st.cls}`}>{st.label}</span></td>
                  <td className="p-3 text-center">
                    {isViewer ? (
                      <button disabled data-testid={`app-view-${r.applicationNumber}`} title="केवळ पाहणे — वैयक्तिक अर्ज पाहण्याची परवानगी नाही"
                        className="inline-flex cursor-not-allowed items-center gap-1 rounded-full border border-slate-200 px-3 py-1.5 text-[12.5px] font-semibold text-slate-300">
                        <Eye className="h-3.5 w-3.5" /> पहा
                      </button>
                    ) : (
                      <button onClick={() => navigate(`/officer/view/${r.id}`)} data-testid={`app-view-${r.applicationNumber}`}
                        className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1.5 text-[12.5px] font-semibold text-[#1b5e3f] hover:border-[#1b5e3f] hover:bg-[#1b5e3f]/5">
                        <Eye className="h-3.5 w-3.5" /> पहा
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[12.5px] text-slate-500">एकूण अर्ज: <strong>{rows.length}</strong></p>
    </OfficerShell>
  );
}
